import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaFileAlt, FaCopy, FaCheck } from 'react-icons/fa';
import { useNotes } from '../context/NoteContext';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
    color: #343a40;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #5a6268;
  }
`;

const NoteContainer = styled.div`
  background-color: ${props => props.color || '#fff8dc'};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const NoteTitleDisplay = styled.h2`
  font-size: 1.75rem;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const NoteContentDisplay = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  white-space: pre-wrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
`;

const LoadingIcon = styled.div`
  font-size: 3rem;
  color: #6c757d;
  margin-bottom: 1rem;
  animation: pulse 1.5s infinite;
  
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.6;
    }
  }
`;

const LoadingText = styled.p`
  font-size: 1.2rem;
  color: #6c757d;
  margin: 0;
`;

const ErrorContainer = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const ErrorTitle = styled.h2`
  font-size: 1.5rem;
  margin: 0 0 0.5rem 0;
`;

const ErrorMessage = styled.p`
  margin: 0 0 1rem 0;
`;

const Toast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #28a745;
  color: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 1000;
  animation: fadeInOut 3s forwards;
  
  @keyframes fadeInOut {
    0% { opacity: 0; transform: translateY(20px); }
    10% { opacity: 1; transform: translateY(0); }
    90% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(-20px); }
  }
`;

const SharedNoteScreen = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { getSharedNote, loading, error } = useNotes();
  
  const [note, setNote] = useState(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  
  useEffect(() => {
    const fetchSharedNote = async () => {
      try {
        const fetchedNote = await getSharedNote(shareToken);
        setNote(fetchedNote);
      } catch (err) {
        console.error('Error fetching shared note:', err);
      }
    };
    
    if (shareToken) {
      fetchSharedNote();
    }
  }, [shareToken, getSharedNote]);
  
  const handleCopyToClipboard = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url)
      .then(() => {
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 3000);
      })
      .catch(err => console.error('Failed to copy:', err));
  };
  
  const handleBack = () => {
    navigate('/login');
  };
  
  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingIcon>
            <FaFileAlt />
          </LoadingIcon>
          <LoadingText>Loading shared note...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <ErrorIcon>❌</ErrorIcon>
          <ErrorTitle>Error Loading Note</ErrorTitle>
          <ErrorMessage>
            {error === 'Note not found' 
              ? 'This shared note could not be found. It may have been deleted or the link is invalid.' 
              : 'There was an error loading this shared note. Please try again later.'}
          </ErrorMessage>
          <ActionButton onClick={handleBack}>
            <FaArrowLeft />
            Go to Login
          </ActionButton>
        </ErrorContainer>
      </Container>
    );
  }
  
  if (!note) {
    return (
      <Container>
        <ErrorContainer>
          <ErrorIcon>❓</ErrorIcon>
          <ErrorTitle>Note Not Found</ErrorTitle>
          <ErrorMessage>
            The shared note you're looking for doesn't exist or the link is invalid.
          </ErrorMessage>
          <ActionButton onClick={handleBack}>
            <FaArrowLeft />
            Go to Login
          </ActionButton>
        </ErrorContainer>
      </Container>
    );
  }
  
  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft />
          </BackButton>
          <Title>Shared Note</Title>
        </HeaderLeft>
        
        <ActionButton onClick={handleCopyToClipboard}>
          <FaCopy />
          Copy Link
        </ActionButton>
      </Header>
      
      <NoteContainer color={note.color || '#fff8dc'}>
        <NoteTitleDisplay>
          {note.title || 'Untitled Note'}
        </NoteTitleDisplay>
        <NoteContentDisplay>
          {note.content || 'No content'}
        </NoteContentDisplay>
      </NoteContainer>
      
      {showCopyToast && (
        <Toast>
          <FaCheck />
          Link copied to clipboard!
        </Toast>
      )}
    </Container>
  );
};

export default SharedNoteScreen; 